/**
 * PersonList - Person management with company aliases and merge
 *
 * Features:
 * - Table view with sorting on all columns
 * - Filters: search, role, has alias, face count
 * - Company-specific alias overrides
 * - Multi-select for merge operation
 * - Create local persons
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  fetchPersons,
  fetchTeams,
  createPerson,
  updatePerson,
  deletePerson,
  searchTeams,
  addTeamMember,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import Spinner from '../../../../shared/components/ui/Spinner';
import { Btn } from '../../../../shared/components/redesign';
import EditPersonDrawer from '../EditPersonDrawer';
import BulkLinkTeamModal from '../BulkLinkTeamModal';
import styles from './PersonList.module.css';

function SortHeader({ label, sortKeyName, sortKey, sortDir, onSort }) {
  return (
    <th
      className={clsx(styles.th, styles.sortable)}
      onClick={() => onSort(sortKeyName)}
    >
      {label}
      {sortKey === sortKeyName && (
        <span className={styles.sortIndicator}>{sortDir === 'asc' ? ' ^' : ' v'}</span>
      )}
    </th>
  );
}

function PersonModal({ person, onSave, onClose, isNew = false }) {
  // Get first team membership if editing existing person
  const firstMembership = person?.team_memberships?.[0];

  const [form, setForm] = useState({
    display_name: person?.display_name || '',
    first_name: person?.first_name || '',
    last_name: person?.last_name || '',
    jersey_number: person?.jersey_number || '',
    notes: person?.notes || '',
    team_id: firstMembership?.team_id || '',
  });
  const [saving, setSaving] = useState(false);

  // Team search state
  const [teamSearchQuery, setTeamSearchQuery] = useState(firstMembership?.team_name || '');
  const [teamSuggestions, setTeamSuggestions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(
    firstMembership ? { id: firstMembership.team_id, name: firstMembership.team_name } : null
  );

  // Debounced team search
  useEffect(() => {
    // Don't search if query matches selected team
    if (selectedTeam && teamSearchQuery === selectedTeam.name) {
      setTeamSuggestions([]);
      return;
    }

    if (!teamSearchQuery || teamSearchQuery.length < 2) {
      setTeamSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await searchTeams(teamSearchQuery);
        setTeamSuggestions(res || []);
      } catch (e) {
        console.error(e);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [teamSearchQuery, selectedTeam]);

  const selectTeam = (team) => {
    setSelectedTeam(team);
    setTeamSearchQuery(team.name);
    setTeamSuggestions([]);
    setForm((prev) => ({ ...prev, team_id: team.id }));
  };

  const clearTeam = () => {
    setSelectedTeam(null);
    setTeamSearchQuery('');
    setTeamSuggestions([]);
    setForm((prev) => ({ ...prev, team_id: '' }));
  };

  const handleSave = async () => {
    if (!form.display_name.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      console.error('Failed to save person:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        {isNew ? 'Create Person' : 'Edit Person'}
      </Modal.Header>
      <Modal.Body>

        <div className={styles.formGroup}>
          <label className={styles.label}>Display Name</label>
          <input
            type="text"
            className={styles.input}
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            placeholder="Full display name"
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>First Name</label>
            <input
              type="text"
              className={styles.input}
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              placeholder="First name"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Last Name</label>
            <input
              type="text"
              className={styles.input}
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              placeholder="Last name"
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Team</label>
            <div className={styles.teamSearchContainer}>
              <div className={styles.teamInputWrapper}>
                <input
                  type="text"
                  className={styles.teamSearchInput}
                  placeholder="Search teams..."
                  value={teamSearchQuery}
                  onChange={(e) => setTeamSearchQuery(e.target.value)}
                  autoComplete="off"
                />
                {selectedTeam && (
                  <button
                    type="button"
                    className={styles.clearTeamBtn}
                    onClick={clearTeam}
                    title="Clear team"
                  >
                    <XMarkIcon className={styles.clearIcon} />
                  </button>
                )}
              </div>
              {teamSuggestions.length > 0 && (
                <div className={styles.teamSuggestionsList}>
                  {teamSuggestions.map((team) => (
                    <button
                      key={team.id}
                      type="button"
                      className={styles.teamSuggestionItem}
                      onClick={() => selectTeam(team)}
                    >
                      <span className={styles.teamName}>{team.name}</span>
                      {team.team_type && (
                        <span className={styles.teamType}>{team.team_type}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Jersey Number</label>
            <input
              type="number"
              className={styles.input}
              value={form.jersey_number || ''}
              onChange={(e) => setForm({ ...form, jersey_number: e.target.value ? parseInt(e.target.value) : '' })}
              placeholder="e.g., 29"
              min={0}
              max={999}
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Notes</label>
          <textarea
            className={styles.textarea}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Internal notes..."
            rows={2}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !form.display_name.trim()}>
          {(() => {
            if (saving) return 'Saving...';
            if (isNew) return 'Create';
            return 'Save';
          })()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function PersonList({ onMerge, activeId = null }) {
  const [persons, setPersons] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [faceCountFilter, setFaceCountFilter] = useState('all');

  // Sorting
  const [sortKey, setSortKey] = useState('display_name');
  const [sortDir, setSortDir] = useState('asc');

  // Selection for merge
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [isNewPerson, setIsNewPerson] = useState(false);
  const [showBulkLink, setShowBulkLink] = useState(false);

  const loadTeams = useCallback(async () => {
    try {
      const data = await fetchTeams({ limit: 500 });
      setTeams(data.results || data || []);
    } catch (err) {
      console.error('Failed to load teams:', err);
    }
  }, []);

  const loadPersons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 500 };
      if (teamFilter !== 'all') {
        params.team = teamFilter;
      }
      const data = await fetchPersons(params);
      setPersons(data.results || data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [teamFilter]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    loadPersons();
  }, [loadPersons]);

  // Filter and sort persons
  const filteredPersons = useMemo(() => {
    let result = persons;

    // Search filter
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.display_name?.toLowerCase().includes(term) ||
          p.first_name?.toLowerCase().includes(term) ||
          p.last_name?.toLowerCase().includes(term) ||
          p.jersey_number?.toString().includes(term)
      );
    }

    // Face count filter (if we have that data)
    if (faceCountFilter === 'no_faces') {
      result = result.filter((p) => !p.face_count || p.face_count === 0);
    } else if (faceCountFilter === 'few_faces') {
      result = result.filter((p) => p.face_count > 0 && p.face_count < 10);
    } else if (faceCountFilter === 'many_faces') {
      result = result.filter((p) => p.face_count >= 10);
    }

    // Sort
    result = [...result].sort((a, b) => {
      let aVal;
      let bVal;

      if (sortKey === 'face_count') {
        aVal = a.face_count || 0;
        bVal = b.face_count || 0;
      } else if (sortKey === 'last_seen') {
        // Missing/null timestamps sort to the bottom in both directions.
        const ta = a.last_seen ? new Date(a.last_seen).getTime() : 0;
        const tb = b.last_seen ? new Date(b.last_seen).getTime() : 0;
        aVal = ta;
        bVal = tb;
      } else {
        const rawA = a[sortKey] || '';
        const rawB = b[sortKey] || '';
        aVal = typeof rawA === 'string' ? rawA.toLowerCase() : rawA;
        bVal = typeof rawB === 'string' ? rawB.toLowerCase() : rawB;
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [persons, search, faceCountFilter, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleEdit = (person) => {
    setEditingPerson(person);
    setIsNewPerson(false);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingPerson(null);
    setIsNewPerson(true);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    // Extract team_id from form data (handled separately via membership)
    const { team_id, ...personData } = formData;
    let personId;

    if (isNewPerson) {
      const person = await createPerson(personData);
      personId = person.id;
    } else if (editingPerson) {
      await updatePerson(editingPerson.id, personData);
      personId = editingPerson.id;
    }

    // Handle team membership
    if (team_id && personId) {
      const jerseyNum = personData.jersey_number || null;
      await addTeamMember(team_id, personId, jerseyNum);
    }

    loadPersons();
  };

  const handleDelete = async (person) => {
    if (!confirm(`Delete person "${person.name}"? This cannot be undone.`)) return;
    try {
      await deletePerson(person.id);
      loadPersons();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredPersons.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPersons.map((p) => p.id)));
    }
  };

  const handleMerge = () => {
    if (selectedIds.size < 2) {
      alert('Select at least 2 persons to merge');
      return;
    }
    const selected = persons.filter((p) => selectedIds.has(p.id));
    if (onMerge) {
      onMerge(selected);
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  if (loading && persons.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading persons...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Persons</h2>
          <p className={styles.subtitle}>
            Manage person display names and aliases for your company
          </p>
        </div>
        <div className={styles.headerActions}>
          {selectedIds.size > 0 && (
            <>
              <Btn kind="ghost" size="sm" onClick={clearSelection}>
                Clear ({selectedIds.size})
              </Btn>
              <Btn kind="primary" size="sm" onClick={handleMerge}>
                Merge selected
              </Btn>
            </>
          )}
          <Btn kind="ghost" size="sm" onClick={() => setShowBulkLink(true)}>
            Bulk link to team…
          </Btn>
          <Btn kind="primary" size="sm" onClick={handleCreate}>
            + New person
          </Btn>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search persons..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className={styles.filterSelect}
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
        >
          <option value="all">All Teams</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name} ({team.member_count ?? 0})
            </option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={faceCountFilter}
          onChange={(e) => setFaceCountFilter(e.target.value)}
        >
          <option value="all">All Face Counts</option>
          <option value="no_faces">No Faces</option>
          <option value="few_faces">1-9 Faces</option>
          <option value="many_faces">10+ Faces</option>
        </select>

        <span className={styles.count}>{filteredPersons.length} persons</span>
      </div>

      {/* Table — Tank Redesign Persons row layout: avatar · name ·
          team · # · faces · last seen · actions. Source:
          design_handoff_tank/screens-rest.jsx § Screen07ManagePersons. */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>
                <input
                  type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === filteredPersons.length}
                  onChange={toggleSelectAll}
                  className={styles.checkbox}
                />
              </th>
              <th className={styles.th} aria-label="Avatar"></th>
              <SortHeader label="Name" sortKeyName="display_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className={styles.th}>Team</th>
              <SortHeader label="#" sortKeyName="jersey_number" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className={styles.th}>Faces</th>
              <SortHeader label="Last seen" sortKeyName="last_seen" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Faces mini-bar normalization — width is a fraction of the
                largest face_count visible in the current filter, so the
                bars give a relative sense of "who dominates" without
                requiring per-month activity data. */}
            {(() => null)()}
            {filteredPersons.map((person) => {
              const initials =
                ((person.first_name || person.display_name || '?')[0] || '?') +
                ((person.last_name || '')[0] || '');
              const teamName = person.team_memberships?.[0]?.team_name;
              const lastSeen = person.last_seen
                ? new Date(person.last_seen).toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })
                : '—';
              const maxFaces = Math.max(
                ...filteredPersons.map((p) => p.face_count || 0),
                1,
              );
              const faceCount = Number.isFinite(person.face_count)
                ? person.face_count
                : 0;
              const facePct = Math.round((faceCount / maxFaces) * 100);
              return (
                <tr
                  key={person.id}
                  className={clsx(
                    styles.row,
                    selectedIds.has(person.id) && styles.selected,
                    // Active state — either an external caller passed an
                    // activeId, or the drawer is open editing this person.
                    (activeId === person.id ||
                      (showModal && editingPerson?.id === person.id)) &&
                      styles.active,
                  )}
                >
                  <td className={styles.td}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(person.id)}
                      onChange={() => toggleSelect(person.id)}
                      className={styles.checkbox}
                    />
                  </td>
                  <td className={styles.td}>
                    <span className={styles.avatarBubble} aria-hidden="true">
                      {initials.toUpperCase()}
                    </span>
                  </td>
                  <td className={styles.td}>
                    <span className={styles.personName}>{person.display_name}</span>
                    {person.aliases?.length > 0 && (
                      <span className={styles.aliasBadge}>Alias</span>
                    )}
                  </td>
                  <td className={styles.td}>{teamName || '—'}</td>
                  <td className={styles.td}>
                    {person.jersey_number ? `#${person.jersey_number}` : '—'}
                  </td>
                  <td className={styles.td}>
                    <span className={styles.facesCell}>
                      <span className={styles.facesCount}>
                        {Number.isFinite(person.face_count)
                          ? person.face_count
                          : '—'}
                      </span>
                      {faceCount > 0 && (
                        <span
                          className={styles.facesBar}
                          aria-hidden="true"
                          title={`${facePct}% of page max`}
                        >
                          <span
                            className={styles.facesBarFill}
                            style={{ width: `${facePct}%` }}
                          />
                        </span>
                      )}
                    </span>
                  </td>
                  <td className={styles.td}>
                    <span className={styles.lastSeen}>{lastSeen}</span>
                  </td>
                  <td className={styles.td}>
                    <div className={styles.actions}>
                      <Btn kind="ghost" size="sm" onClick={() => handleEdit(person)}>
                        Edit
                      </Btn>
                      <Btn kind="danger" size="sm" onClick={() => handleDelete(person)}>
                        Delete
                      </Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredPersons.length === 0 && (
              <tr>
                <td colSpan={8} className={styles.empty}>
                  No persons found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Person drawer — 560 px right slide-in (was a centered modal).
          Source: design_handoff_tank manage-redesign-v2 brief; chrome
          mirrors MatchDetails for design-system parity. */}
      {showModal && (
        <EditPersonDrawer
          person={editingPerson}
          isNew={isNewPerson}
          onSave={handleSave}
          onDelete={async (p) => {
            await handleDelete(p);
            setShowModal(false);
            setEditingPerson(null);
          }}
          onClose={() => {
            setShowModal(false);
            setEditingPerson(null);
          }}
        />
      )}

      {showBulkLink && (
        <BulkLinkTeamModal
          onComplete={() => loadPersons()}
          onClose={() => setShowBulkLink(false)}
        />
      )}
    </div>
  );
}
